import {buildTourProgressElement, postJson} from './product-tours.js'

export const CASELOAD_TOUR_ID = 'nurse_patient_list_caseload'
export const CASELOAD_TOUR_EVENT = `cpm:tour:${CASELOAD_TOUR_ID}`

const rootSelector = '[data-cy="patient-activities"]'
const tableSelector = '#table-patient_call_list_table'
let activeTour = null

function tableHeader(root, label) {
    return [...(root.querySelector(tableSelector)?.querySelectorAll('thead th') ?? [])]
        .find(header => header.textContent.replace(/\s+/g, ' ').trim().toLowerCase() === label.toLowerCase())
}

function button(root, label) {
    return [...root.querySelectorAll('button')]
        .find(control => control.textContent.replace(/\s+/g, ' ').trim() === label)
}

export const CASELOAD_TOUR_STEPS = [
    {
        id: 'worklist',
        title: 'Your daily call worklist',
        body: 'This table brings your scheduled patient work into one place. Start with the activity date and call window, then open a patient when you are ready to work on their task. The tour only explains the controls; it will not change patient records.',
        target: root => root.querySelector('h2'),
        on: 'top',
    },
    {
        id: 'add-patients',
        title: 'Add patients when you have room',
        body: 'Use Self-Assign Patients above this table to see patients available for pickup. When shown, the number tells you how many are available. Choose the patients you can call, select Assign Patients to Me, then confirm. If the button is unavailable, check its message: there may be no patients ready you may need to finish calls in your current caseload first, or your caseload may be at your current limit, which grows as you consistently complete cases.',
        target: (root, documentObject) => documentObject.querySelector('[data-cy="caseload-add-patients"]') ?? root.querySelector('h2'),
        on: 'bottom',
        example: {
            label: 'Example only: adding a patient',
            flow: ['Self-Assign Patients', 'Select available patients', 'Assign Patients to Me', 'Confirm'],
        },
    },
    {
        id: 'search',
        title: 'Find the work you need',
        body: 'Search by task, patient, or practice to narrow the list. Clear the search to return to the full worklist. Search helps you find an assignment; it does not change it.',
        target: root => root.querySelector('input[placeholder^="Search for:"]'),
        on: 'top',
    },
    {
        id: 'sort',
        title: 'Put the next calls first',
        body: 'Select a column heading to sort. Activity Date is a useful starting point for today; the applied sorting label shows your current order, and Clear resets it. You can sort by patient, attempts, call window, and other sortable columns too.',
        target: root => tableHeader(root, 'Activity Date') ?? tableHeader(root, 'RN Activity Date'),
        fallback: root => button(root, 'Columns'),
        on: 'top',
    },
    {
        id: 'columns',
        title: 'Choose the details you need',
        body: 'Columns lets you show or hide table details. Keep the fields you use to plan calls visible, such as date, call window, attempts, and practice. You can change this view without changing any patient information.',
        target: root => button(root, 'Columns'),
        on: 'top',
    },
    {
        id: 'assignment',
        title: 'Read and open an assignment',
        body: 'Task tells you what kind of work is due, and Program shows the care program. When rows are available, select a patient name to open the patient workflow and review the chart before calling.',
        target: root => tableHeader(root, 'Patient'),
        fallback: root => button(root, 'Columns'),
        on: 'top',
    },
    {
        id: 'reminder',
        title: 'Check reminder status',
        body: 'When available, Next Call Reminder Status shows whether a call reminder is planned or sent. It is a status indicator, not a prompt to send another reminder.',
        target: root => tableHeader(root, 'Next Call Reminder Status'),
        on: 'top',
        optional: true,
    },
    {
        id: 'attempts',
        title: 'See what outreach has happened',
        body: 'Call Attempts Completed counts unsuccessful attempts or shows Successful Call after a completed call. Use it with the activity date to decide which scheduled patients need attention next.',
        target: root => tableHeader(root, 'Call Attempts Completed'),
        fallback: root => button(root, 'Columns'),
        on: 'top',
    },
    {
        id: 'call-window',
        title: 'Call at the right time',
        body: 'Activity Date is the scheduled day. Start and End show the preferred call window, and Zone is the patient’s time zone. An ASAP row has no fixed end time; always check the patient details before calling.',
        target: root => tableHeader(root, 'Start'),
        fallback: root => button(root, 'Columns'),
        on: 'top',
    },
    {
        id: 'history',
        title: 'Use recent call history',
        body: 'Last Successful Clinical Call and Last Date Called tell you when contact last happened. Total Time and # of Successful Calls give context for work already completed. These fields help you prepare; review the chart for clinical details.',
        target: root => tableHeader(root, 'Last Date Called'),
        fallback: root => button(root, 'Columns'),
        on: 'top',
    },
    {
        id: 'practice',
        title: 'Check the care location',
        body: 'Practice and Location help you recognize where a patient receives care. Use them when coordinating the right workflow or finding patients from a particular practice.',
        target: root => tableHeader(root, 'Practice'),
        fallback: root => button(root, 'Columns'),
        on: 'top',
    },
    {
        id: 'select-patients',
        title: 'Choose patients for an action',
        body: 'Tick the box beside each patient you want to include. In the example, Patient A is left out and Patient B is selected. You can use the box at the top to select multiple rows. The Actions menu then appears; check the selection before choosing an action.',
        target: root => root.querySelector(`${tableSelector} thead input[type="checkbox"]`) ?? root.querySelector('h2'),
        on: 'top',
        example: {
            label: 'Example only — your patient list is unchanged',
            flow: ['☐ Patient A', '☑ Patient B', 'Actions ▾'],
        },
    },
    {
        id: 'release-patients',
        title: 'Release patients you cannot call',
        body: 'Select patients, then Actions → Release Patients if shown. Temporary lets another nurse cover them for the current month; the patient returns to your caseload on the 1st of the following month. Permanent removes the patient from your caseload permanently. Choose a reason and release early in the month so another nurse has time to pick them up and call.',
        target: root => root.querySelector(`${tableSelector} thead input[type="checkbox"]`) ?? root.querySelector('h2'),
        on: 'top',
        modalExample: 'release',
    },
    {
        id: 'send-reminders',
        title: 'Send a call reminder',
        body: 'Select patients, then Actions → Send Call Reminder if shown. Choose the day before the call, now, or a date and time. A chosen date also moves the next call to the following day, so review it before confirming.',
        target: root => root.querySelector(`${tableSelector} thead input[type="checkbox"]`) ?? root.querySelector('h2'),
        on: 'top',
        modalExample: 'reminder',
    },
    {
        id: 'pages',
        title: 'Move through the full list',
        body: 'Choose how many rows to show per page. When your caseload spans pages, use the page controls below the table to see the rest; a patient missing from this page may be on another.',
        target: root => root.querySelector('#patient_call_list_table-perPage'),
        on: 'top',
    },
]

function exampleElement(documentObject, tag, className, textContent = '') {
    const element = documentObject.createElement(tag)
    element.className = className
    element.textContent = textContent
    return element
}

function exampleRadio(documentObject, label, selected) {
    const row = exampleElement(documentObject, 'div', `flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] leading-4 ${selected ? 'border-cyan-400 bg-cyan-50/40 font-semibold text-slate-800' : 'border-slate-200 bg-white text-slate-700'}`)
    const circle = exampleElement(documentObject, 'span', `flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selected ? 'border-cyan-700' : 'border-slate-300'}`)
    circle.setAttribute('aria-hidden', 'true')
    if (selected) circle.append(exampleElement(documentObject, 'span', 'h-1.5 w-1.5 rounded-full bg-cyan-700'))
    row.append(circle, exampleElement(documentObject, 'span', '', label))
    return row
}

function buildModalExample(documentObject, kind) {
    const example = exampleElement(documentObject, 'div', 'mt-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5 text-slate-700')
    example.setAttribute('data-tour-example', kind)
    example.setAttribute('aria-label', kind === 'release' ? 'Example release window; no patient is affected' : 'Example call reminder window; no reminder is sent')

    const caption = exampleElement(documentObject, 'p', 'mb-2 text-[11px] font-semibold text-slate-600', kind === 'release' ? 'Example only — no patient is affected' : 'Example only — no reminder is sent')
    const window = exampleElement(documentObject, 'div', 'space-y-1.5 rounded-lg border border-slate-200 bg-white p-3 shadow-sm')

    if (kind === 'release') {
        const title = exampleElement(documentObject, 'p', 'border-b border-slate-200 pb-2 text-sm font-semibold text-slate-900', 'Release 1 Patient')
        const reason = exampleElement(documentObject, 'p', 'pt-1 text-[11px] font-medium text-slate-700', 'Release Reason:')
        const select = exampleElement(documentObject, 'div', 'flex items-center justify-between rounded-md border border-blue-500 bg-white px-2.5 py-1.5 text-[11px] text-slate-500', 'Choose a reason')
        select.append(exampleElement(documentObject, 'span', 'text-slate-400', '⌄'))
        const choices = exampleElement(documentObject, 'div', 'rounded-md border border-slate-200 bg-white text-[11px] leading-5 text-slate-700')
        const reasons = ['Call date/time mismatch', 'Spanish speaking patient', 'Other']
        reasons.forEach(choice => {
            choices.append(exampleElement(documentObject, 'div', 'px-2.5 py-0.5', choice))
        })
        window.append(title, exampleRadio(documentObject, 'Temporary Release', true), exampleRadio(documentObject, 'Permanent Release', false), reason, select, choices)
    } else {
        const title = exampleElement(documentObject, 'p', 'flex items-center gap-2 text-sm font-semibold text-slate-900')
        const notice = exampleElement(documentObject, 'span', 'flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-50 text-xs font-bold text-amber-600', '!')
        notice.setAttribute('aria-hidden', 'true')
        title.append(notice, exampleElement(documentObject, 'span', '', 'Send Next Call Reminder SMS'))
        const subtitle = exampleElement(documentObject, 'p', 'border-b border-slate-200 pb-2 text-[11px] leading-4 text-slate-500', 'Choose when the reminder should be sent.')
        const time = exampleElement(documentObject, 'div', 'ml-5 border-l-2 border-cyan-200 pl-2.5 text-[11px] text-slate-700')
        time.append(exampleElement(documentObject, 'p', 'font-medium', 'Time to send SMS'))
        time.append(exampleElement(documentObject, 'div', 'mt-1 rounded-md border border-slate-300 px-2 py-1 text-slate-400', 'Select a time'))
        const footer = exampleElement(documentObject, 'div', 'flex justify-end gap-2 border-t border-slate-200 pt-2 text-[10px] font-semibold')
        footer.append(exampleElement(documentObject, 'span', 'rounded-md border border-slate-300 px-2 py-1 text-slate-600', 'Cancel'))
        footer.append(exampleElement(documentObject, 'span', 'rounded-md bg-primary-solid px-2 py-1 text-white', 'Schedule Reminder'))
        window.append(title, subtitle, exampleRadio(documentObject, 'Send the day before the scheduled call day', true), time, exampleRadio(documentObject, 'Send now', false), exampleRadio(documentObject, 'Send on date', false), footer)
    }

    example.append(caption, window)
    return example
}

function stepContent(documentObject, step, index, total) {
    const content = documentObject.createElement('div')
    if (step.modalExample) {
        content.className = 'max-h-[calc(100dvh-12rem)] overflow-y-auto overscroll-contain'
        content.setAttribute('tabindex', '0')
        content.setAttribute('aria-label', 'Tour guidance and example')
    }
    content.append(buildTourProgressElement(index, total, documentObject))

    const body = documentObject.createElement('p')
    body.className = 'nurse-performance-tour-body'
    body.textContent = step.body
    content.append(body)

    if (step.modalExample) content.append(buildModalExample(documentObject, step.modalExample))

    if (step.example) {
        const example = documentObject.createElement('div')
        example.className = 'mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700'
        example.setAttribute('aria-label', step.example.label)

        const label = documentObject.createElement('p')
        label.className = 'mb-2 font-semibold text-slate-600'
        label.textContent = step.example.label

        const flow = documentObject.createElement('div')
        flow.className = 'flex flex-wrap items-center gap-1.5'
        step.example.flow.forEach((item, itemIndex) => {
            if (itemIndex > 0) {
                const arrow = documentObject.createElement('span')
                arrow.className = 'text-slate-400'
                arrow.textContent = '→'
                flow.append(arrow)
            }

            const tile = documentObject.createElement('span')
            tile.className = 'rounded-md border border-slate-200 bg-white px-2 py-1 font-medium text-slate-700'
            tile.textContent = item
            flow.append(tile)
        })
        example.append(label, flow)

        if (step.example.choices) {
            const choices = documentObject.createElement('div')
            choices.className = 'mt-2 space-y-1 border-t border-slate-200 pt-2 text-slate-600'
            step.example.choices.forEach(choice => {
                const row = documentObject.createElement('p')
                row.textContent = choice
                choices.append(row)
            })
            example.append(choices)
        }

        content.append(example)
    }

    return content
}

export function availableCaseloadSteps(root) {
    return CASELOAD_TOUR_STEPS.filter(step => {
        if (!step.optional) return true

        const target = step.target(root, root.ownerDocument)
        return target && target.getClientRects().length > 0
    })
}

export function startCaseloadTour(browserWindow, documentObject, ShepherdLibrary) {
    if (activeTour || ShepherdLibrary.activeTour) {
        return false
    }

    const root = documentObject.querySelector(rootSelector)
    if (!root || !root.querySelector(tableSelector)) {
        return false
    }

    const steps = availableCaseloadSteps(root)
    if (!steps.length) {
        return false
    }

    const tour = new ShepherdLibrary.Tour({
        defaultStepOptions: {
            cancelIcon: {enabled: true},
            classes: 'nurse-performance-tour',
            modalOverlayOpeningPadding: 10,
            modalOverlayOpeningRadius: 16,
            scrollTo: true,
            scrollToHandler: element => {
                element.scrollIntoView({behavior: 'instant', block: 'center', inline: 'center'})
                browserWindow.setTimeout(() => {
                    if (activeTour?.getCurrentStep()?.target === element) {
                        element.scrollIntoView({behavior: 'instant', block: 'center', inline: 'center'})
                    }
                }, 500)
            },
        },
        exitOnEsc: true,
        exitOnOverlayClick: true,
        useModalOverlay: true,
    })
    activeTour = tour

    steps.forEach((step, index) => {
        const isFirst = index === 0
        const isLast = index === steps.length - 1
        tour.addStep({
            id: `caseload-${step.id}`,
            attachTo: {
                element: () => {
                    const currentRoot = documentObject.querySelector(rootSelector)
                    if (!currentRoot) return null

                    const target = step.target(currentRoot, documentObject)
                    return target?.getClientRects().length ? target : (step.fallback?.(currentRoot) ?? currentRoot)
                },
                on: step.on,
            },
            canClickTarget: false,
            title: step.title,
            text: stepContent(documentObject, step, index, steps.length),
            buttons: [
                isFirst
                    ? {text: 'Not now', classes: 'shepherd-button-secondary', action: () => tour.cancel()}
                    : {text: 'Back', classes: 'shepherd-button-secondary', action: () => tour.back()},
                {text: isLast ? 'Done' : (isFirst ? 'Start tour' : 'Next'), action: () => isLast ? tour.complete() : tour.next()},
            ],
        })
    })

    const cleanup = () => {
        if (activeTour === tour) activeTour = null
    }
    tour.on('complete', () => {
        cleanup()
        postJson(browserWindow, `/tours/${CASELOAD_TOUR_ID}/complete`)
    })
    tour.on('cancel', () => {
        cleanup()
        postJson(browserWindow, `/tours/${CASELOAD_TOUR_ID}/dismiss`)
    })

    tour.start()

    return true
}

export function registerCaseloadTour(browserWindow, documentObject, ShepherdLibrary) {
    const start = () => startCaseloadTour(browserWindow, documentObject, ShepherdLibrary)
    browserWindow.addEventListener(CASELOAD_TOUR_EVENT, start)

    return () => browserWindow.removeEventListener(CASELOAD_TOUR_EVENT, start)
}
